import React, { useEffect, useRef, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2, AlertCircle, RefreshCw } from "lucide-react";

/**
 * MediaStatusBadge — small pill that shows the server-side processing state
 * (transcode + poster extraction) for a media URL. Polls `/api/admin/media/by-path`
 * every 2s while processing; goes quiet once `ready` / `skipped` / `failed`.
 *
 *   pending / processing → spinner + "Processing…"
 *   ready                → green check + "Optimized" + Re-process button
 *   failed               → red alert + error tooltip + Retry button
 *   skipped              → nothing rendered (not all uploads need transcode)
 *
 * Props:
 *  - url:     the /api/files/… URL we want status for. Non-/api/files URLs
 *             (e.g. arbitrary http(s) pastes) are ignored — only uploaded
 *             objects can be in the pipeline.
 *  - mediaId: optional shortcut — if the parent has just uploaded and knows
 *             the media_id, we can poll by id directly without a path lookup.
 *  - compact: if true, render the icon-only pill (no text). Default false.
 *  - onReady: optional callback fired once when status flips to 'ready'.
 */
const POLL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;       // stop polling after 5 minutes
const FILES_PREFIX = "/api/files/";

function pathFromUrl(url) {
  if (!url) return null;
  if (url.startsWith(FILES_PREFIX)) return url.slice(FILES_PREFIX.length);
  // Absolute URL pointing back to our own ingress?
  try {
    const u = new URL(url);
    const idx = u.pathname.indexOf(FILES_PREFIX);
    if (idx !== -1) return u.pathname.slice(idx + FILES_PREFIX.length);
  } catch (_) { /* not a URL */ }
  return null;
}

export default function MediaStatusBadge({ url, mediaId, compact = false, onReady }) {
  const [status, setStatus] = useState(null);   // server response, or null
  const [retrying, setRetrying] = useState(false);
  const startTsRef = useRef(0);
  const onReadyFiredRef = useRef(false);

  useEffect(() => {
    if (!url && !mediaId) { setStatus(null); return undefined; }
    onReadyFiredRef.current = false;
    startTsRef.current = performance.now();
    let cancelled = false;
    let timer = null;

    const fetchOnce = async () => {
      if (cancelled) return;
      try {
        const res = mediaId
          ? await api.get(`/admin/media/${mediaId}`)
          : (() => {
              const p = pathFromUrl(url);
              if (!p) return null;
              return api.get(`/admin/media/by-path/${encodeURI(p)}`);
            })();
        if (!res) { setStatus(null); return; }
        const data = (await res).data;
        if (cancelled) return;
        setStatus(data);
        if (data.processing_status === "ready" && !onReadyFiredRef.current) {
          onReadyFiredRef.current = true;
          onReady?.(data);
        }
        const done = ["ready", "failed", "skipped"].includes(data.processing_status);
        const tooLong = performance.now() - startTsRef.current > POLL_TIMEOUT_MS;
        if (!done && !tooLong) {
          timer = setTimeout(fetchOnce, POLL_MS);
        }
      } catch (e) {
        // 404 means we can't track this URL — silently hide the badge.
        if (e?.response?.status === 404) {
          setStatus(null);
          return;
        }
        // Other errors: back off and retry once.
        if (!cancelled) timer = setTimeout(fetchOnce, POLL_MS * 2);
      }
    };
    fetchOnce();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [url, mediaId, onReady]);

  const reprocess = async () => {
    if (!status?.id) return;
    setRetrying(true);
    try {
      await api.post(`/admin/media/${status.id}/reprocess`);
      toast.success("Re-processing started");
      // Reset polling immediately
      setStatus({ ...status, processing_status: "pending" });
      startTsRef.current = performance.now();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally { setRetrying(false); }
  };

  if (!status) return null;
  const s = status.processing_status;
  if (s === "skipped") return null;

  const isProcessing = s === "pending" || s === "processing";
  const isReady = s === "ready";
  const isFailed = s === "failed";

  const base = "inline-flex items-center gap-1.5 rounded-full text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 transition";
  const cls = isReady
    ? `${base} bg-emerald-50 text-emerald-700 border border-emerald-200`
    : isFailed
      ? `${base} bg-rose-50 text-rose-700 border border-rose-200`
      : `${base} bg-amber-50 text-amber-700 border border-amber-200`;

  const icon = isReady ? <CheckCircle2 className="h-3 w-3" />
    : isFailed ? <AlertCircle className="h-3 w-3" />
      : <Loader2 className="h-3 w-3 animate-spin" />;

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="media-status-badge" data-status={s}>
      <span className={cls} title={isFailed ? (status.processing_error || "") : ""}>
        {icon}
        {!compact && (isReady ? "Optimized" : isFailed ? "Failed" : "Processing…")}
        {!compact && isReady && status.faststart_ok === false && (
          <span className="ml-1 text-amber-700">(no-faststart)</span>
        )}
      </span>
      {(isFailed || isReady) && status.id && (
        <button
          type="button"
          onClick={reprocess}
          disabled={retrying || isProcessing}
          data-testid="media-reprocess-btn"
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-zinc-600 hover:text-zinc-900 disabled:opacity-60 transition"
          title={isFailed ? status.processing_error || "Retry transcode" : "Re-optimize"}
        >
          {retrying ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          {isFailed ? "Retry" : "Re-optimize"}
        </button>
      )}
      {!compact && isReady && status.optimization_warning && (
        <span className="text-[10px] text-zinc-500" data-testid="media-status-optimal">
          already optimal
        </span>
      )}
      {!compact && isReady && !status.optimization_warning && status.processed_size && status.original_size && (
        <span className="text-[10px] text-zinc-500" data-testid="media-status-size">
          {formatPct(status.original_size, status.processed_size)} · {formatKB(status.processed_size)}
        </span>
      )}
      {!compact && isFailed && status.processing_error && (
        <span className="text-[10px] text-rose-700 max-w-xs truncate" title={status.processing_error}>
          {status.processing_error}
        </span>
      )}
    </div>
  );
}

function formatKB(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatPct(orig, processed) {
  if (!orig || !processed) return "";
  const ratio = processed / orig;
  if (ratio < 1) return `−${Math.round((1 - ratio) * 100)}%`;
  return `+${Math.round((ratio - 1) * 100)}%`;
}
